import React, { useCallback, useRef, useState } from 'react';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { Column, ColumnEventParams } from 'primereact/column';
import Table from '../../../../components/Table/Table';
import {
  formatCellTextToAmount,
  formatCurrency,
  formatPercentage,
  roundCurrency,
} from '../../../../../utils/formatUtils';
import { DataTableRowMouseEventParams } from 'primereact/datatable';
import { animateCellText } from '../../../../../utils/htmlUtils';
import { ContractProgressItem } from '../../../../Interfaces/Accounting/ContractBillings.interface';
import CurrencyLabelInput from '../../../../components/inputs/StyledInputs/CurrencyLabelInput';
import ErrorToast, { showToast } from '../../../../components/messages/ErrorAlert';
import { Toast } from 'primereact/toast';
import ColumnTextEditor from '../../../../components/Table/ColumnTextEditor';
import { Access } from '../../../../Interfaces/admin/Role.interfaces';
import { Checkbox } from 'primereact/checkbox';

type ContractProgressTableProps = {
  items: ContractProgressItem[];
  releasedRetainage?: number;
  isApproval?: boolean;
  access: Access;
  updateIsEditing?: (value: boolean) => void;
  updateBillingDraft?: (item: number, value?: number) => void;
  updateReleasedRetainage?: (retainage?: number) => void;
  changePurchasedSMDraft?: (item: number, value?: number) => void;
  changeProductionSMDraft?: (item: number, value?: number) => void;
  changeIncludeSmValues?: (show: boolean) => void;
  showSMValues?: boolean;
  hideSMValuesCheckbox?: boolean;
  blockSMValuesCheckbox?: boolean;
};

const ContractProgressTable = ({
  items,
  releasedRetainage,
  isApproval,
  access,
  updateIsEditing = (value: boolean) => {},
  changeIncludeSmValues,
  showSMValues,
  hideSMValuesCheckbox,
  blockSMValuesCheckbox,
  ...updates
}: ContractProgressTableProps) => {
  const [checkedPercentage, setCheckedPercentage] = useState(true);

  const { selectedCompany } = useCompanyContext();
  const canEdit = (!!access.editable || (access.shouldApprove && isApproval)) && !!selectedCompany?.isActive;
  const toast = useRef<Toast>(null);

  const calcHeight = (rows: number) => {
    const headerHeight = 37;
    const footerHeight = 54;
    const rowHeight = 31;
    return headerHeight + footerHeight + rows * rowHeight + 3;
  };

  const onRowHover = (e: DataTableRowMouseEventParams) => {
    const cell = e.originalEvent.target as HTMLElement;
    const row = cell.closest('tr') as HTMLElement;
    animateCellText(row);
  };

  const onCellEditComplete = (e: ColumnEventParams) => {
    const { rowData, newValue, field } = e;

    try {
      let valueText: string = newValue?.toString();

      if (checkedPercentage && newValue) {
        valueText = `${valueText}%`;
      }

      let isRemaining = false;
      if (valueText?.includes('%')) {
        const cleanedValue = parseFloat(valueText.replace('%', ''));
        const remainderPercentage =
          100 - Math.round(rowData.CompletedRatio * 10000) / 100;

        isRemaining = cleanedValue === remainderPercentage;
      }

      const amount = isRemaining
        ? rowData.BalanceRemaining
        : formatCellTextToAmount({
            text: valueText,
            limitAmount: rowData.BalanceRemaining,
            pTotal: rowData.ContractAmount,
            exceedErrorMessage:
              'The amount entered exceeds the Contract Amount',
          });

      const roundedAmount = Math.round(amount * 100) / 100;

      if (
        rowData.ContractAmount > 0 &&
        rowData.BilledAmount + roundedAmount < 0
      ) {
        throw Error('The total billed amount cannot be lower than 0.');
      } else if (
        rowData.ContractAmount < 0 &&
        rowData.BilledAmount + roundedAmount > 0
      ) {
        throw Error('The total billed amount cannot be higher than 0.');
      }

      rowData[field] = roundedAmount || null;

      const updateBillingDraft = updates.updateBillingDraft;
      if (!updateBillingDraft) {
        updateIsEditing(false);
        return;
      }

      const item = rowData.Item.trim();
      if (roundedAmount) {
        updateBillingDraft(item, roundedAmount);
      } else {
        updateBillingDraft(item);
      }
    } catch (e) {
      const error = e as { message: string };
      showToast('error', toast, 'Edit Amount Error', error.message, 3000);
    }

    updateIsEditing(false);
  };

  const onCurrentSMCellEditComplete = (e: ColumnEventParams) => {
    const { rowData, newValue, field } = e;

    try {
      const roundedAmount = roundCurrency(newValue ?? 0);
      const newRemainingSM = roundCurrency(
        (rowData['SM'] ?? 0) +
          (roundedAmount ?? 0) -
          (rowData['currentProductionSM'] ?? 0)
      );

      rowData[field] = roundedAmount || null;
      rowData['remainingSM'] =
        newRemainingSM && (roundedAmount || rowData['currentProductionSM'])
          ? newRemainingSM
          : null;

      const changePurchasedSMDraft = updates.changePurchasedSMDraft;
      if (!changePurchasedSMDraft) {
        updateIsEditing(false);
        return;
      }

      const item = rowData.Item.trim();
      if (roundedAmount) {
        changePurchasedSMDraft(item, roundedAmount);
      } else {
        changePurchasedSMDraft(item);
      }
    } catch (e) {
      const error = e as { message: string };
      showToast('error', toast, 'Edit Amount Error', error.message, 3000);
    }

    updateIsEditing(false);
  };

  const onCurrentProductionSMCellEditComplete = (e: ColumnEventParams) => {
    const { rowData, newValue, field } = e;

    try {
      const roundedAmount = roundCurrency(newValue ?? 0);

      if ((rowData['SM'] ?? 0) < (roundedAmount ?? 0)) {
        throw Error(
          `The “Moved SM to Production” value cannot be greater than the “To Date SM” value!`
        );
      }

      const newRemainingSM = roundCurrency(
        (rowData['SM'] ?? 0) +
          (rowData['currentPurchasedSM'] ?? 0) -
          (roundedAmount ?? 0)
      );
      rowData[field] = roundedAmount || null;
      rowData['remainingSM'] =
        newRemainingSM && (roundedAmount || rowData['currentPurchasedSM'])
          ? newRemainingSM
          : null;

      const changeProductionSMDraft = updates.changeProductionSMDraft;
      if (!changeProductionSMDraft) {
        updateIsEditing(false);
        return;
      }

      const item = rowData.Item.trim();
      if (roundedAmount) {
        changeProductionSMDraft(item, roundedAmount);
      } else {
        changeProductionSMDraft(item);
      }
    } catch (e) {
      const error = e as { message: string };
      showToast('error', toast, 'Edit Amount Error', error.message, 3000);
    }

    updateIsEditing(false);
  };

  let contractAmount = 0;
  let billed = 0;
  let balance = 0;
  let retainage = 0;
  let sm = 0;
  let installedSM = 0;
  let retSM = 0;
  items.forEach((item: ContractProgressItem) => {
    contractAmount = contractAmount + item.ContractAmount;
    billed = billed + item.BilledAmount;
    balance = balance + item.BalanceRemaining;
    retainage = retainage + item.Retainage;
    sm += item.SM ?? 0;
    installedSM += item.Installed ?? 0;
    retSM += item.SMRetg ?? 0;
  });

  return (
    <React.Fragment>
      <ErrorToast toastRef={toast} />
      <Table
        id="cb-contract-progress"
        data={items}
        className={`mx-3 dashboardOptionShadow hoverEffect ${
          showSMValues ? 'dobleHeader' : 'checkboxHeader'
        }`}
        calcHeight={useCallback(calcHeight, [])}
        onRowMouseEnter={useCallback(onRowHover, [])}
        hideColumns={true}
        headerContent={
          !hideSMValuesCheckbox ? (
            <div className="flex align-items-center gap-1 text-18px blackText md:ml-4">
              Fill out SM Fields?
              <Checkbox
                onChange={(e) => {
                  if (changeIncludeSmValues) {
                    changeIncludeSmValues(e.checked);
                  }
                }}
                className="printHide"
                checked={showSMValues}
                disabled={blockSMValuesCheckbox}
                tooltip={
                  blockSMValuesCheckbox
                    ? 'Remove all SM values to enable the checkbox.'
                    : undefined
                }
              />
            </div>
          ) : undefined
        }
      >
        <Column
          headerClassName={`tableHeader`}
          className="tableCell p-0 printHide"
          footerClassName="tableFooter "
        />
        <Column
          field="Contract"
          header="Contract #"
          style={{ minWidth: '140px', maxWidth: '140px' }}
          headerClassName="tableHeader font-normal"
          className="justify-content-center text-standard blackText tableCell"
          footerClassName="tableFooter"
        />
        <Column
          field="ContractName"
          header="Contract Name"
          body={(cb) => {
            return <div className="scroll-text">{cb.ContractName}</div>;
          }}
          style={{ minWidth: '250px', maxWidth: '250px' }}
          headerClassName="tableHeader font-normal justify-content-center"
          className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="tableFooter"
        />
        <Column
          field="Item"
          header="Item"
          headerClassName={`tableHeader font-normal`}
          style={{ minWidth: '100px', maxWidth: '100px' }}
          className={`justify-content-center text-standard blackText tableCell`}
          footerClassName="tableFooter"
          sortable
        />
        <Column
          field="Description"
          header="Description"
          body={(cb) => {
            return <div className="scroll-text">{cb.Description}</div>;
          }}
          style={{ minWidth: '300px', maxWidth: '300px' }}
          headerClassName="tableHeader font-normal justify-content-center"
          className="text-standard blackText tableCell overflow-x-hidden white-space-nowrap checkOverflow"
          footerClassName="overflow-x-visible tableFooter block text-right border-top-2 border-transparent mt-4 limitBorder relative"
          footer="Total"
          sortable
        />
        <Column
          field="ContractAmount"
          header="Contract Amount"
          body={(cb) => formatCurrency(cb.ContractAmount)}
          style={{ minWidth: '160px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-end text-standard blackText tableCell"
          footer={formatCurrency(contractAmount)}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
          sortable
        />
        <Column
          field="BilledAmount"
          header="Billed"
          body={(cb) => formatCurrency(cb.BilledAmount)}
          style={{ minWidth: '150px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-end text-standard blackText tableCell"
          footer={formatCurrency(billed)}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
          sortable
        />
        <Column
          field="CompletedRatio"
          header="% Complet"
          body={(cb) => formatPercentage(cb.CompletedRatio * 100)}
          style={{ minWidth: '140px' }}
          headerClassName="tableHeader font-normal justify-content-center text-center"
          className="justify-content-end text-standard blackText tableCell"
          footerClassName="tableFooter block text-right border-top-2 mt-4"
          footer={formatPercentage((billed / contractAmount || 0) * 100)}
          sortable
        />
        <Column
          field="BalanceRemaining"
          header="Balance to Bill"
          body={(cb) =>
            cb.BalanceRemaining ? formatCurrency(cb.BalanceRemaining) : '-'
          }
          style={{ minWidth: '160px' }}
          headerClassName="tableHeader font-normal text-center justify-content-center"
          className="justify-content-end text-standard blackText tableCell"
          footer={formatCurrency(balance)}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
          sortable
        />
        <Column
          field="NewBilling"
          header="Billing this Period"
          body={(cb) =>
            !canEdit || !cb.BalanceRemaining ? null : cb.NewBilling ? (
              <div
                className={`w-full text-right totalColor cursor-pointer printColor`}
              >
                {formatCurrency(cb.NewBilling)}
              </div>
            ) : (
              <div className="w-full cursor-pointer flex justify-content-center">
                <i className="pi pi-pencil totalColor printHide" />
              </div>
            )
          }
          style={{ minWidth: '160px' }}
          headerClassName="tableHeader font-normal text-center"
          className={`justify-content-center text-standard blackText tableCell`}
          onBeforeCellEditShow={() => {
            updateIsEditing(true);
          }}
          editor={(options) => {
            if (canEdit && options.rowData.BalanceRemaining) {
              let roundedAmount = 0;
              const { value, ...otherOptions } = options;
              let val = value;

              if (checkedPercentage && value) {
                const percentage =
                  (value / options.rowData.ContractAmount) * 100;
                roundedAmount = Math.round(percentage * 100) / 100;
                val = roundedAmount;
              }

              return (
                <ColumnTextEditor
                  id={`TBillField${options.rowData.Item.trim()}`}
                  value={val}
                  isPercentage={checkedPercentage}
                  {...otherOptions}
                />
              );
            }

            return null;
          }}
          onCellEditComplete={(options) =>
            canEdit && options.rowData.BalanceRemaining
              ? onCellEditComplete(options)
              : null
          }
          footer={(e) => {
            const records = e.props.value;
            let total = 0;
            records.forEach((record: ContractProgressItem) => {
              const amount = record.NewBilling ?? 0;
              total = total + amount;
            });

            return total ? formatCurrency(total) : '';
          }}
          footerClassName="tableFooter block text-right border-top-2 mt-4"
        />
        <Column
          header={
            <div className="flex gap-2 align-items-center">
              Only Percentage?
              <Checkbox
                onChange={(e) => {
                  setCheckedPercentage(e.checked);
                }}
                className="printHide"
                checked={checkedPercentage}
              />
            </div>
          }
          style={{ minWidth: '180px' }}
          headerClassName="tableHeader font-normal text-center"
          className={`justify-content-center text-standard blackText tableCell printHide`}
          footerClassName="tableFooter"
        />
        {showSMValues && (
          <Column
            field="SM"
            header="To Date SM"
            body={(cb) => (cb.SM ? formatCurrency(cb.SM) : '-')}
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footer={formatCurrency(sm)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        {showSMValues && (
          <Column
            field="Installed"
            header="Installed SM to Date"
            body={(cb) => (cb.Installed ? formatCurrency(cb.Installed) : '-')}
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footer={formatCurrency(installedSM)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        {showSMValues && (
          <Column
            field="SMRetg"
            header="SM Retainage"
            body={(cb) => (cb.SMRetg ? formatCurrency(cb.SMRetg) : '-')}
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footer={formatCurrency(retSM)}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        {showSMValues && (
          <Column
            field="currentPurchasedSM"
            header="Purchased SM During Current Period"
            body={(cb) =>
              !canEdit ? null : cb.currentPurchasedSM ? (
                <div
                  className={`w-full text-right totalColor cursor-pointer printColor`}
                >
                  {formatCurrency(cb.currentPurchasedSM)}
                </div>
              ) : (
                <div className="w-full cursor-pointer flex justify-content-center">
                  <i className="pi pi-pencil totalColor printHide" />
                </div>
              )
            }
            onBeforeCellEditShow={() => {
              updateIsEditing(true);
            }}
            editor={(options) => {
              if (canEdit) {
                const { value, ...otherOptions } = options;

                return (
                  <ColumnTextEditor
                    id={`TFCurrentPurchasedField${options.rowData.Item.trim()}`}
                    value={value}
                    {...otherOptions}
                    blockPercentage
                    allowOnlyPositive
                  />
                );
              }

              return null;
            }}
            onCellEditComplete={(options) =>
              canEdit ? onCurrentSMCellEditComplete(options) : null
            }
            footer={(e) => {
              const records = e.props.value;
              let total = 0;
              records.forEach((record: ContractProgressItem) => {
                const amount = record.currentPurchasedSM ?? 0;
                total = total + amount;
              });

              return total ? formatCurrency(total) : '';
            }}
            style={{ minWidth: '200px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center white-space-normal bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        {showSMValues && (
          <Column
            field="currentProductionSM"
            header="Moved SM to production during current period"
            body={(cb) =>
              !canEdit || !cb.SM ? null : cb.currentProductionSM ? (
                <div
                  className={`w-full text-right totalColor cursor-pointer printColor`}
                >
                  {formatCurrency(cb.currentProductionSM)}
                </div>
              ) : (
                <div className="w-full cursor-pointer flex justify-content-center">
                  <i className="pi pi-pencil totalColor printHide" />
                </div>
              )
            }
            onBeforeCellEditShow={() => {
              updateIsEditing(true);
            }}
            editor={(options) => {
              if (canEdit && options.rowData.SM) {
                const { value, ...otherOptions } = options;

                return (
                  <ColumnTextEditor
                    id={`TFCurrentProduction${options.rowData.Item.trim()}`}
                    value={value}
                    {...otherOptions}
                    blockPercentage
                    allowOnlyPositive
                  />
                );
              }

              return null;
            }}
            onCellEditComplete={(options) =>
              canEdit && options.rowData.SM
                ? onCurrentProductionSMCellEditComplete(options)
                : null
            }
            footer={(e) => {
              const records = e.props.value;
              let total = 0;
              records.forEach((record: ContractProgressItem) => {
                const amount = record.currentProductionSM ?? 0;
                total = total + amount;
              });

              return total ? formatCurrency(total) : '';
            }}
            style={{ minWidth: '200px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center white-space-normal bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        {showSMValues && (
          <Column
            field="remainingSM"
            header="Remaining SM after this billing"
            body={(cb) =>
              cb.remainingSM ? formatCurrency(cb.remainingSM) : '-'
            }
            style={{ minWidth: '160px' }}
            headerClassName="tableHeader font-normal text-center justify-content-center white-space-normal bg-blue-300"
            className="justify-content-end text-standard blackText tableCell"
            footer={(e) => {
              const records = e.props.value;
              let total = 0;
              records.forEach((record: ContractProgressItem) => {
                const amount = record.remainingSM ?? 0;
                total = total + amount;
              });

              return total ? formatCurrency(total) : '';
            }}
            footerClassName="tableFooter block text-right border-top-2 mt-4"
            sortable
          />
        )}
        <Column
          headerClassName={`tableHeader`}
          className="tableCell p-0 printHide"
          footerClassName="tableFooter "
        />
      </Table>
      <div className="ml-auto w-fit mr-5 my-5 flex flex-wrap justify-content-center align-items-center gap-3 text-standard">
        <div>Unreleased Retainage</div>
        <div id="unreleasedRetainage" className="w-11rem text-right">
          {formatCurrency(retainage)}
        </div>
        <div id="retainagePeriod" className="w-11rem h-2rem">
          <CurrencyLabelInput
            pTotal={retainage}
            limit={retainage}
            exceedErrorMessage="The amount entered exceeds the Unreleased Retainage"
            blockEdit={!canEdit}
            defaultValue={releasedRetainage}
            changeValue={updates.updateReleasedRetainage}
            onInputStart={() => {
              updateIsEditing(true);
            }}
            onInputEnd={() => {
              updateIsEditing(false);
            }}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

const transactionsAreEqual = (
  prevTransactions: Readonly<ContractProgressTableProps>,
  nextTransactions: Readonly<ContractProgressTableProps>
) => {
  return (
    prevTransactions.items === nextTransactions.items &&
    prevTransactions.releasedRetainage === nextTransactions.releasedRetainage &&
    prevTransactions.showSMValues === nextTransactions.showSMValues &&
    prevTransactions.blockSMValuesCheckbox ===
      nextTransactions.blockSMValuesCheckbox
  );
};

export default React.memo(ContractProgressTable, transactionsAreEqual);
